import {Box} from '@mantine/core';
import {useDisclosure} from '@mantine/hooks';
import Image from 'next/image';
import {useRouter} from 'next/router';
import React from 'react';
import {GSSKT_HOST} from '../../../../../config/constants/hosts';
import type {Recruitment} from '../../../../../core/repositories/recruitingInfoRepository';
import {cn} from '../../../../../lib/classname';
import {CommonLink} from '../../../../../shared/components/CommonLink';
import {
  ENTRY_BUTTON_ID,
  GradientEntryButton,
} from '../../../../../shared/components/EntryButton/GradientEntryButton';
import {appendQueryParam} from '../../../../../shared/services/appendQueryParam';
import {buildScheduleGtmContent} from '../../../../../shared/services/buildScheduleGtmContent';
import {ScheduleButtonNewDesign} from '../../../../recruitment/components/ScheduleButton';
import {isEntryRegisterModalExperimentEnabled} from '../../../usecase/entryRegisterModalExperiment';
import {useCalendarPromptModal} from '../../../usecase/useCalendarPromptModal';
import {CompanyLogoMenu} from '../CompanyLogoMenu';
import {EntryRegisterModal} from '../EntryRegisterModal';
import {Deadline} from './Deadline';
import {FollowCompletionModal} from './FollowCompletionModal';
import {Schedules} from './Schedules';
import {Tags} from './Tags';

type Props = {
  recruitment: Recruitment;
  className?: string;
  /** 未ログイン時のエントリー押下でその場に登録モーダルを出す（募集一覧のみ） */
  withEntryRegisterModal?: boolean;
};

export const CardForSp: React.FC<Props> = ({
  recruitment,
  className,
  withEntryRegisterModal = false,
}) => {
  const {asPath} = useRouter();
  const [
    registerModalOpened,
    {open: openRegisterModal, close: closeRegisterModal},
  ] = useDisclosure(false);
  // VWO のバリエーション側だけモーダルを出す。VWO は非同期に読み込まれるのでクリック時に判定する
  const handleUnauthenticatedEntryClick = () => {
    if (!isEntryRegisterModalExperimentEnabled()) {
      return false;
    }
    openRegisterModal();
    return true;
  };
  const {
    modalVisible,
    suppressChecked,
    setSuppressChecked,
    handleCalendarClose,
    handleFollowSuccess,
  } = useCalendarPromptModal();

  const onFollowSuccess = () => {
    const firstSchedule = recruitment.schedules[0];
    if (!firstSchedule) {
      return;
    }
    handleFollowSuccess(firstSchedule.isCalendarRegistered);
  };

  const recruitingInfoPage = `/recruiting_info/${recruitment.category === 'archive' ? 'archives' : 'view'}/${recruitment.detailID}`;

  const openCompanyPage = (id: number) => {
    if (id === 0) {
      return;
    }

    window.open(`/company/${id}`, '_blank', 'noopener,noreferrer');
  };

  const entryButtonUrl = recruitment.entryURL
    ? appendQueryParam(recruitment.entryURL, 'source_url', recruitingInfoPage)
    : '';
  const canShowEntryButton =
    entryButtonUrl &&
    ['medium', 'large'].includes(recruitment.companies[0]?.pageType ?? '');

  return (
    <>
      <Box className={cn('w-full bg-white p-16', className)}>
        <div className="mb-24 [&:nth-last-child(1)]:mb-0 pb-24 [&:nth-last-child(1)]:pb-0 border-b-[1px] border-line border-collapse [&:nth-last-child(1)]:border-b-0">
          <div className="flex flex-col">
            <div className="flex w-full">
              {recruitment.category === 'client' &&
                recruitment.companies[0]?.logoURLs?.['140x105'] && (
                  <CompanyLogoMenu
                    companyId={recruitment.companies[0]?.id ?? 0}
                    companyName={recruitment.companies[0]?.name ?? ''}
                    isFollowed={recruitment.companies[0]?.isFollowed ?? false}
                    followerCount={recruitment.companies[0]?.followerCount ?? 0}
                    onFollowSuccess={onFollowSuccess}
                  >
                    <Image
                      className="h-[62px] w-[90px] bg-white object-contain relative cursor-pointer mb-4"
                      src={recruitment.companies[0]?.logoURLs?.['140x105']}
                      alt={recruitment.title}
                      width={62}
                      height={90}
                    />
                  </CompanyLogoMenu>
                )}
              <Box className="ml-auto mb-4 flex items-center gap-8">
                {recruitment.schedules.length > 0 &&
                  recruitment.schedules[0] && (
                    <>
                      <Deadline schedule={recruitment.schedules[0]} />
                      <ScheduleButtonNewDesign
                        scheduleId={recruitment.schedules[0].id}
                        isRegisteredInitial={
                          recruitment.schedules[0].isCalendarRegistered
                        }
                        registeredCountInitial={
                          recruitment.schedules[0].calendarRegisteredCount
                        }
                        buttonStyle="icon"
                        recruitmentData={{
                          companyId: recruitment.companies[0]?.id ?? 0,
                          companyName: recruitment.companies[0]?.name ?? '',
                          entryTitle: recruitment.title,
                          detailURL: `${GSSKT_HOST}${recruitment.detailPageURL}`,
                        }}
                        dataGtmClick="recruitment-schedule-add"
                        dataGtmContent={buildScheduleGtmContent({
                          scheduleId: recruitment.schedules[0].id,
                          location: 'recruiting-info-card-sp',
                        })}
                      />
                    </>
                  )}
              </Box>
            </div>
            <div className="justify-start items-center inline-flex">
              <div className={'flex-col justify-start gap-2 inline-flex'}>
                <div className="">
                  <CommonLink
                    href={recruitingInfoPage}
                    data-gtm-click-recruitment-detail-button="true"
                  >
                    <Box className="text-md text-black-1 font-bold line-clamp-2 hover:opacity-40">
                      {recruitment.title}
                    </Box>
                  </CommonLink>
                  <p
                    className="text-base text-black-1 mt-4 hover:opacity-40 line-clamp-2"
                    onClick={() => {
                      openCompanyPage(recruitment.companies[0]?.id ?? 0);
                    }}
                  >
                    {recruitment.companies.map((company, i) => (
                      <React.Fragment key={company.id}>
                        {i > 0 && ', '}
                        <span>{company.name}</span>
                      </React.Fragment>
                    ))}
                  </p>
                </div>
              </div>
            </div>
          </div>
          <Box className="py-8">
            <Tags
              tags={recruitment.tags}
              targetYear={recruitment.targetYear}
              type={recruitment.type}
              groupedJobTypes={recruitment.groupedJobTypes}
            />
          </Box>
          {recruitment.schedules.length > 0 && recruitment.schedules[0] && (
            <Box className="py-4">
              <Schedules
                schedules={[recruitment.schedules[0]]}
                recruitmentData={{
                  companyId: recruitment.companies[0]?.id ?? 0,
                  companyName: recruitment.companies[0]?.name ?? '',
                  detailURL: `${GSSKT_HOST}${recruitment.detailPageURL}`,
                }}
              />
            </Box>
          )}
          <Box className="mt-[5px] flex justify-between items-start w-full">
            <Box>
              {recruitment.schedules.length > 1 && (
                <CommonLink href={recruitingInfoPage}>
                  <Box className="text-base text-black-1 whitespace-nowrap cursor-pointer hover:opacity-50 pl-12">
                    ほか{recruitment.schedules.length - 1}日程
                  </Box>
                </CommonLink>
              )}
            </Box>
            <Box className="flex gap-8">
              <CommonLink
                href={recruitingInfoPage}
                data-gtm-click-recruitment-detail-button="true"
              >
                <Box className="px-16 py-8 bg-line text-black text-sm font-bold rounded-[8px] shadow-card hover:opacity-80">
                  募集詳細
                </Box>
              </CommonLink>
              {canShowEntryButton && (
                <GradientEntryButton
                  entryUrl={entryButtonUrl}
                  id={ENTRY_BUTTON_ID.RECRUITING_INFO}
                  className="px-16 h-[34px]"
                  withShadow
                  companyId={recruitment.companies[0]?.id}
                  companyName={recruitment.companies[0]?.name}
                  entryTitle={recruitment.title}
                  deadline={recruitment.schedules[0]?.entryEnd}
                  onUnauthenticatedClick={
                    withEntryRegisterModal
                      ? handleUnauthenticatedEntryClick
                      : undefined
                  }
                />
              )}
            </Box>
          </Box>
        </div>
      </Box>
      {withEntryRegisterModal && canShowEntryButton && (
        <EntryRegisterModal
          opened={registerModalOpened}
          onClose={closeRegisterModal}
          recruitment={recruitment}
          entryUrl={entryButtonUrl}
          recruitingInfoPage={recruitingInfoPage}
        />
      )}
      {recruitment.schedules[0] && (
        <FollowCompletionModal
          opened={modalVisible}
          onClose={handleCalendarClose}
          recruitmentId={recruitment.id}
          recruitmentTitle={recruitment.title}
          scheduleId={recruitment.schedules[0].id}
          isCalendarRegistered={recruitment.schedules[0].isCalendarRegistered}
          calendarRegisteredCount={
            recruitment.schedules[0].calendarRegisteredCount
          }
          companyId={recruitment.companies[0]?.id ?? 0}
          companyName={recruitment.companies[0]?.name ?? ''}
          detailURL={`${GSSKT_HOST}${recruitment.detailPageURL}`}
          pagePath={asPath}
          suppressChecked={suppressChecked}
          onSuppressChange={setSuppressChecked}
        />
      )}
    </>
  );
};
